import { useMemo, useState } from "react";
import { TextField } from "@mui/material";

import { useTranslation } from "@/lib/i18n";
import Button from "@/components/button/button";
import Icon from "@/components/icon/icon";
import Stack from "@/components/stack/stack";
import CheckCard, {
  StatusLabel,
} from "@/features/widget-forms/forms/weather-model-map/components/check-card/check-card";

import styles from "./weather-model-map-dialog.module.css";
import { useModelMapLayersQuery, useModelParametersQuery, useModelsQuery } from "./queries";

export type PickModelWizardOutput = {
  title: string;
  model_id: string;
};

type PickModelWizardProps = {
  onComplete: (output: PickModelWizardOutput) => void;
  onCancel: () => void;
};

/**
 * Model + title step before the react-final-form editor mounts. Keeps wizard fields out of the
 * main {@link Form} tree.
 */
export function PickModelWizard({ onComplete, onCancel }: PickModelWizardProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>(undefined);

  const { data: models } = useModelsQuery();

  const selectedModel = useMemo(
    () => models?.find((model) => model.id === selectedModelId),
    [models, selectedModelId],
  );

  const { data: parameters } = useModelParametersQuery(selectedModelId);

  const { data: mapLayers } = useModelMapLayersQuery(selectedModelId);

  const canSubmit = Boolean(title.trim() && selectedModelId);

  const selectedStatus: StatusLabel = {
    text: t("admin.widgets.fields.model_map.map_layers.selected"),
    icon: "check",
  };

  const handleNext = () => {
    if (!selectedModelId || !title.trim()) return;
    onComplete({ title: title.trim(), model_id: selectedModelId });
  };

  return (
    <div className={styles.pickModelLayout}>
      <div className={styles.header}>
        <div className={styles.titleDisplayRow}>
          <div className={styles.titleValue}>
            {t("admin.widgets.fields.map_editor.pick_model_wizard.title")}
          </div>
        </div>
        <div className={styles.subtitle}>
          {t("admin.widgets.fields.map_editor.pick_model_wizard.subtitle")}
        </div>
      </div>
      <div className={styles.pickModelBody}>
        <div>
          <TextField
            autoFocus
            required
            label={t("admin.widgets.fields.name.label")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            variant="standard"
            fullWidth
          />
        </div>
        <div>
          <span className={styles.pickFieldLabel}>
            {t("admin.widgets.fields.model_map.general.model_label")}
          </span>
          <div className={styles.pickModelList}>
            {models?.map((model) => (
              <CheckCard
                key={model.id}
                title={model.name}
                checked={selectedModelId === model.id}
                onClick={() => setSelectedModelId(model.id)}
                className={styles.pickModelItem}
                statusLabel={selectedModelId === model.id ? selectedStatus : undefined}
              >
                {model.description}
              </CheckCard>
            ))}
          </div>
        </div>

        {selectedModel && (
          <div className={styles.modelInfo}>
            <div className={styles.modelInfoGroup}>
              <h2>{t("admin.widgets.fields.model_map.general.available_map_layers")}</h2>
              {mapLayers?.length ? (
                <ul>
                  {mapLayers?.map((mapLayer) => (
                    <li key={mapLayer.id}>{mapLayer.label}</li>
                  ))}
                </ul>
              ) : (
                <span className={styles.infoLabel}>
                  {t("admin.widgets.fields.model_map.general.none")}
                </span>
              )}
            </div>
            <div className={styles.modelInfoGroup}>
              <h2>{t("admin.widgets.fields.model_map.general.available_parameters")}</h2>
              {parameters?.length ? (
                <ul>
                  {parameters?.map((parameter) => (
                    <li key={parameter.id}>{parameter.label}</li>
                  ))}
                </ul>
              ) : (
                <span className={styles.infoLabel}>
                  {t("admin.widgets.fields.model_map.general.none")}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <Stack justifyContent="flex-end" gap={8}>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t("general.cancel")}
          </Button>
          <Button type="button" variant="solid" onClick={handleNext} disabled={!canSubmit}>
            {t("general.next")}
            <Icon name="chevron-right" />
          </Button>
        </Stack>
      </div>
    </div>
  );
}
